import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PropertyDetailsStep from "@/components/valuation/PropertyDetailsStep";
import ContactInformationStep from "@/components/valuation/ContactInformationStep";
import ValuationResultsStep from "@/components/valuation/ValuationResultsStep";
import ValuationHeader from "@/components/public/ValuationHeader";
import Footer from "@/pages/marketing/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface PropertyData {
  address: string;
  city: string;
  state: string;
  zip_code: string;
  property_type?: string;
  bedrooms?: number;
  bathrooms?: number;
  square_feet?: number;
}

export interface ContactData {
  full_name: string;
  email: string;
  phone_number: string;
  consent_to_contact: boolean;
}

export interface ValuationResults {
  estimated_value: number;
  value_low: number;
  value_high: number;
  formatted_value: string;
  formatted_range: string;
}

const PropertyValuationPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [propertyData, setPropertyData] = useState<PropertyData | null>(null);
  const [contactData, setContactData] = useState<ContactData | null>(null);
  const [valuationResults, setValuationResults] = useState<ValuationResults | null>(null);
  const [leadId, setLeadId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handlePropertySubmit = (data: PropertyData) => {
    setPropertyData(data);
    setCurrentStep(2);
    window.scrollTo(0, 0);
  };

  const handleContactSubmit = async (data: ContactData) => {
    setContactData(data);
    setIsLoading(true);

    try {
      const { data: result, error } = await supabase.functions.invoke('get-property-valuation', {
        body: {
          ...propertyData,
          ...data,
          user_agent: navigator.userAgent,
        }
      });

      if (error) throw error;

      if (result.success) {
        setValuationResults(result.valuation);
        setLeadId(result.lead_id);
        setCurrentStep(3);
        window.scrollTo(0, 0);
      } else {
        throw new Error(result.error || 'Failed to get valuation');
      }
    } catch (error) {
      console.error('Error getting valuation:', error);
      toast.error('Failed to get property valuation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToPropertyDetails = () => {
    setCurrentStep(1);
    window.scrollTo(0, 0);
  };

  return (
    <div className="min-h-screen bg-godyspo-background">
      <ValuationHeader />
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-2">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
              currentStep >= 1 ? 'bg-godyspo-gold text-godyspo-background' : 'bg-muted text-muted-foreground'
            } font-semibold`}>
              1
            </div>
            <div className={`h-1 w-16 ${currentStep >= 2 ? 'bg-godyspo-gold' : 'bg-muted'}`} />
            <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
              currentStep >= 2 ? 'bg-godyspo-gold text-godyspo-background' : 'bg-muted text-muted-foreground'
            } font-semibold`}>
              2
            </div>
            <div className={`h-1 w-16 ${currentStep >= 3 ? 'bg-godyspo-gold' : 'bg-muted'}`} />
            <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
              currentStep >= 3 ? 'bg-godyspo-gold text-godyspo-background' : 'bg-muted text-muted-foreground'
            } font-semibold`}>
              3
            </div>
          </div>
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            <span className={currentStep === 1 ? 'text-godyspo-gold' : ''}>Property Details</span>
            <span className={currentStep === 2 ? 'text-godyspo-gold' : ''}>Contact Info</span>
            <span className={currentStep === 3 ? 'text-godyspo-gold' : ''}>Your Valuation</span>
          </div>
        </div>

        {/* Step Content */}
        {currentStep === 1 && (
          <PropertyDetailsStep 
            onSubmit={handlePropertySubmit}
            initialData={propertyData}
          />
        )}

        {currentStep === 2 && (
          <ContactInformationStep
            onSubmit={handleContactSubmit}
            onBack={handleBackToPropertyDetails}
            isLoading={isLoading}
            initialData={contactData}
          />
        )}

        {currentStep === 3 && valuationResults && (
          <ValuationResultsStep
            valuation={valuationResults}
            leadId={leadId}
            onNavigateToConsumer={() => navigate('/consumer')}
          />
        )}
      </div>
      <Footer />
    </div>
  );
};

export default PropertyValuationPage;
